import { sanitizeHtml } from '../lib/sanitize';
import { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { useLangPrefix, withLangPrefix } from '../hooks/useLangPrefix';
import { Button } from '../components/ui/button';
import { ArrowLeft, Clock, Tag, Share2, Calendar } from 'lucide-react';
import { toast } from 'sonner';

const CATEGORY_LABELS = {
  automobile: 'Sellerie automobile',
  nautique: 'Sellerie nautique',
  moto: 'Sellerie moto',
  habitat: 'Habitat & médical',
  metier: 'Métier & atelier',
  general: 'Édito',
};

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' });
  } catch (_) { return ''; }
};

const upsertMeta = (selector, attr, content) => {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement('meta');
    const [_, attrs] = selector.match(/\[(.+)\]/) || [];
    if (attrs) {
      const [k, v] = attrs.split('=');
      el.setAttribute(k, v.replace(/['"]/g, ''));
    }
    document.head.appendChild(el);
  }
  el.setAttribute(attr, content);
};

const upsertJsonLd = (data) => {
  let el = document.head.querySelector('script[data-blog-jsonld]');
  if (!el) {
    el = document.createElement('script');
    el.type = 'application/ld+json';
    el.setAttribute('data-blog-jsonld', '1');
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
};

const upsertCanonical = (url) => {
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', url);
};

export const BlogPostPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { lang } = useLangPrefix();
  const L = (p) => withLangPrefix(p, lang);

  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        const data = await api.getBlogPost(slug);
        if (!mounted) return;
        setPost(data);
        // SEO meta
        document.title = data.meta_title || data.title;
        upsertMeta('meta[name="description"]', 'content', data.meta_description || data.excerpt);
        upsertMeta('meta[property="og:title"]', 'content', data.meta_title || data.title);
        upsertMeta('meta[property="og:description"]', 'content', data.meta_description || data.excerpt);
        upsertMeta('meta[property="og:image"]', 'content', data.hero_image || '');
        upsertMeta('meta[property="og:type"]', 'content', 'article');
        upsertCanonical(window.location.href);
        upsertJsonLd({
          "@context": "https://schema.org",
          "@type": "BlogPosting",
          "headline": data.title,
          "description": data.meta_description || data.excerpt,
          "image": data.hero_image,
          "author": { "@type": "Person", "name": data.author },
          "publisher": {
            "@type": "Organization",
            "name": "Sellerie Académie",
            "logo": { "@type": "ImageObject", "url": window.location.origin + "/logo192.png" }
          },
          "datePublished": data.published_at,
          "dateModified": data.updated_at,
          "mainEntityOfPage": { "@type": "WebPage", "@id": window.location.href },
          "keywords": (data.tags || []).join(", "),
        });
        // Track visit (fire-and-forget)
        api.trackBlogVisit(slug, lang, document.referrer || '', '');
        // Load related
        try {
          const list = await api.listBlogPosts({ category: data.category, limit: 4 });
          if (mounted) setRelated((list || []).filter((p) => p.slug !== data.slug).slice(0, 3));
        } catch (_) {}
      } catch (e) {
        toast.error("Article introuvable");
        navigate(L('/blog'), { replace: true });
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  // Cleanup head tags when leaving
  useEffect(() => () => {
    const j = document.head.querySelector('script[data-blog-jsonld]');
    if (j) j.remove();
  }, []);

  const handleShare = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try { await navigator.share({ title: post?.title, text: post?.excerpt, url }); } catch (_) {}
    } else {
      try {
        await navigator.clipboard.writeText(url);
        toast.success("Lien copié dans le presse-papier");
      } catch (_) { toast.error("Impossible de copier le lien"); }
    }
  };

  if (loading || !post) {
    return (
      <div className="min-h-screen pt-40 flex items-center justify-center bg-[#FAF7F0]">
        <div className="animate-spin w-8 h-8 border-2 border-[#C9B896] border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <article className="min-h-screen bg-[#FAF7F0]" data-testid={`blog-post-${post.slug}`}>
      {/* Hero header */}
      <header className="relative pt-32 lg:pt-40 pb-12 lg:pb-16 bg-white border-b border-[#1F2A4A]/10">
        <div className="max-w-3xl mx-auto px-6 lg:px-0">
          <Link to={L('/blog')} className="inline-flex items-center gap-2 text-sm text-[#1F2A4A]/60 hover:text-[#C9B896] transition-colors mb-8" data-testid="blog-back-link">
            <ArrowLeft className="w-4 h-4" /> Tous les articles
          </Link>
          <div className="flex items-center gap-3 mb-6 text-xs font-mono uppercase tracking-[0.25em] text-[#C9B896]">
            <span>{CATEGORY_LABELS[post.category] || post.category}</span>
            <span className="w-1 h-1 rounded-full bg-[#C9B896]" />
            <span className="text-[#1F2A4A]/50 flex items-center gap-1.5"><Clock className="w-3 h-3" /> {post.reading_time}&nbsp;min</span>
          </div>
          <h1 className="font-serif text-3xl md:text-5xl lg:text-6xl font-light leading-[1.02] text-[#1F2A4A] tracking-tight">
            {post.title}
          </h1>
          <p className="mt-7 text-lg md:text-xl text-[#1F2A4A]/70 leading-relaxed font-light">
            {post.excerpt}
          </p>
          <div className="flex flex-wrap items-center justify-between gap-4 mt-10 pt-6 border-t border-[#1F2A4A]/10">
            <div className="flex items-center gap-3 text-sm text-[#1F2A4A]/60">
              <div className="w-10 h-10 rounded-full bg-[#C9B896]/30 flex items-center justify-center text-[#1F2A4A] font-medium">
                {post.author?.[0] || 'S'}
              </div>
              <div>
                <p className="font-medium text-[#1F2A4A]">{post.author}</p>
                <p className="text-xs flex items-center gap-1.5"><Calendar className="w-3 h-3" /> {formatDate(post.published_at)}</p>
              </div>
            </div>
            <button onClick={handleShare} className="inline-flex items-center gap-2 text-sm text-[#1F2A4A] hover:text-[#C9B896] transition-colors" data-testid="blog-share-btn">
              <Share2 className="w-4 h-4" /> Partager
            </button>
          </div>
        </div>
      </header>

      {/* Hero image */}
      {post.hero_image && (
        <div className="max-w-5xl mx-auto px-6 lg:px-0 -mt-2">
          <div className="aspect-[16/9] overflow-hidden rounded-b-3xl">
            <img src={post.hero_image} alt={post.hero_image_alt || post.title} className="w-full h-full object-cover" />
          </div>
        </div>
      )}

      {/* Body */}
      <div className="max-w-3xl mx-auto px-6 lg:px-0 py-16 lg:py-20">
        <div
          className="blog-prose font-body text-[#1F2A4A]/85 text-lg leading-[1.8]"
          dangerouslySetInnerHTML={{ __html: sanitizeHtml(post.content_html) }}
        />
        {/* Tags */}
        {post.tags?.length > 0 && (
          <div className="mt-14 pt-8 border-t border-[#1F2A4A]/10">
            <p className="text-xs font-mono uppercase tracking-[0.25em] text-[#C9B896] mb-3">Mots-clés</p>
            <div className="flex flex-wrap gap-2">
              {post.tags.map((tag) => (
                <span key={tag} className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 bg-[#1F2A4A]/5 text-[#1F2A4A]/70 rounded-full">
                  <Tag className="w-3 h-3" /> {tag}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* CTA */}
      <section className="bg-[#1F2A4A] text-[#E8DCC8] py-16">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <p className="text-xs font-mono uppercase tracking-[0.4em] text-[#C9B896] mb-3">PASSER À L'ATELIER</p>
          <h3 className="font-serif text-3xl md:text-4xl font-light text-white leading-tight mb-5">
            Reproduire le geste, <span className="italic text-[#C9B896]">en vrai</span>.
          </h3>
          <p className="text-white/65 mb-7 max-w-xl mx-auto">
            Tu peux lire — ou tu peux venir le faire à Istres. Sessions limitées à 8 stagiaires, certification Qualiopi, financements possibles.
          </p>
          <Link to={L('/formations-sur-place')}>
            <Button className="bg-[#C9B896] text-[#1F2A4A] hover:bg-[#E0D4BA] font-medium px-7 py-5 rounded-full" data-testid="blog-cta-apply">
              Voir les sessions à venir →
            </Button>
          </Link>
        </div>
      </section>

      {/* Related */}
      {related.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 lg:px-12 py-20">
          <p className="text-xs font-mono uppercase tracking-[0.3em] text-[#C9B896] mb-3">À LIRE ENSUITE</p>
          <h3 className="font-serif text-3xl md:text-4xl text-[#1F2A4A] font-light mb-12">Dans la même rubrique.</h3>
          <div className="grid md:grid-cols-3 gap-10">
            {related.map((p) => (
              <Link key={p.slug} to={L(`/blog/${p.slug}`)} className="group" data-testid={`blog-related-${p.slug}`}>
                <div className="aspect-[4/3] rounded-2xl overflow-hidden mb-4">
                  <img src={p.hero_image} alt={p.hero_image_alt || p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <p className="text-xs font-mono uppercase tracking-[0.2em] text-[#C9B896] mb-2">{CATEGORY_LABELS[p.category] || p.category}</p>
                <h4 className="font-serif text-xl text-[#1F2A4A] group-hover:text-[#C9B896] transition-colors leading-snug">{p.title}</h4>
              </Link>
            ))}
          </div>
        </section>
      )}
    </article>
  );
};

export default BlogPostPage;
